/** @jsxImportSource @emotion/react */
import { Container, LoadingIcon, Text, useTheme } from "basis";
import { getHref } from "library/api";
import { downloadFile } from "library/downloadFile";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import { SvgInline } from "../../SvgInline/SvgInline";
import { Statement as StatementType } from "./types";

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString(undefined, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

type Props = {
  statement: StatementType;
  isFirst: boolean;
};

export const Statement = ({ statement, isFirst }: Props) => {
  const theme = useTheme();
  const [isRequested, setIsRequested] = useState(false);
  const objectUrlRef = useRef<string>();
  const href = statement.actions.find(
    ({ rel }) => rel === "download-pdf",
  )?.href;
  const statementInfo = useQuery(
    ["statement", { href }],
    async () => {
      const { data } = await getHref(href, { responseType: "blob" });

      return data;
    },
    {
      enabled: isRequested,
      onSuccess: (data) => {
        objectUrlRef.current = window.URL.createObjectURL(data);
        const fileName = `${formatDate(statement.statement_date).replace(
          /\s+/g,
          "_",
        )}_statement.pdf`;
        downloadFile({ fileName, href: objectUrlRef.current });
      },
      onSettled: () => {
        setIsRequested(false);
      },
    },
  );

  useEffect(
    () => () => {
      if (objectUrlRef.current) {
        window.URL.revokeObjectURL(objectUrlRef.current);
      }
    },
    [],
  );

  return (
    <div
      css={{
        borderTop: isFirst ? "none" : `1px solid ${theme.colors.grey.t10}`,
      }}
    >
      <button
        type="button"
        css={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: `${theme.space[2]} ${theme.space[11]} ${theme.space[2]} ${theme.space[6]}`,
          ...theme.getTextStyleCSS("body1"),
          ":hover:not(:disabled)": {
            cursor: "pointer",
            backgroundColor: theme.colors.secondary.lightBlue.t25,
          },
          ":focus": {
            outline: "none",
          },
        }}
        disabled={statementInfo.isFetching}
        onClick={() => {
          setIsRequested(true);
        }}
      >
        {statementInfo.isFetching ? (
          <div
            css={{
              width: theme.space[8],
              height: theme.space[8],
              paddingTop: 6,
            }}
          >
            <LoadingIcon size="small" />
          </div>
        ) : (
          <SvgInline name="document" size="32" />
        )}
        <strong
          css={{
            marginLeft: theme.space[2],
          }}
        >
          {formatDate(statement.statement_date)}
        </strong>
      </button>
      {statementInfo.isError && (
        <Container padding="0 0 2 13">
          <Text textStyle="body2" color="conditional.negative.text">
            We were unable to load this statement. Please try again later.
          </Text>
        </Container>
      )}
    </div>
  );
};
